import { Layer, LayerProps } from "./mapLibre/Layer"
import { useMapContext } from "@/components/maps/mapLibre/MapContext"
import { AlternativeRoute } from "@/lib/planRouteParser"
import * as polyline from "@mapbox/polyline"
import { Feature, Geometry } from "geojson"
import { Source } from "@/components/maps/mapLibre/Source"
import { getRouteColorsFromLabel } from "@/lib/routeColors"

type HoveredRouteHighlightProps = {
  routeId: string
  alternativeRoutes: AlternativeRoute[] | undefined
}

function HoveredRouteHighlight({
  routeId,
  alternativeRoutes,
}: HoveredRouteHighlightProps) {
  const mapContext = useMapContext()
  const map = mapContext?.getMapInstance()

  if (!alternativeRoutes || !routeId || !map) {
    return null
  }

  const hoveredRoute = alternativeRoutes.find(
    (route) => route.routeInfo.routes[0].id === routeId,
  )

  if (!hoveredRoute) {
    return null
  }

  // Get the route label to determine colors
  const routeLabel = hoveredRoute.routeInfo.routes[0].label
  const colors = getRouteColorsFromLabel(routeLabel)

  const coordinates = hoveredRoute.routeInfo.routes[0].legs.reduce<
    [number, number][]
  >((acc, leg) => {
    if (leg.polyline) {
      const legCoordinates = polyline
        .decode(leg.polyline)
        .map(([lat, lng]) => [lng, lat] as [number, number])

      return [...acc, ...legCoordinates]
    }
    return acc
  }, [])

  const sourceId = `hovered-route-source`

  const feature: Feature<Geometry, { [p: string]: any }> = {
    type: "Feature",
    id: 0,
    properties: {},
    geometry: {
      type: "LineString",
      coordinates,
    },
  }

  // Main line layer - innermost route type color
  const layer = {
    id: `hovered-route`,
    type: "line",
    layout: {
      "line-cap": "butt",
      "line-join": "round",
    },
    paint: {
      "line-color": colors.main, // Use route type color
      "line-opacity": 1,
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 5, 16, 9],
    },
  } as LayerProps

  // White middle layer
  const layerWhite = {
    id: `hovered-route-white`,
    type: "line",
    layout: {
      "line-cap": "butt",
      "line-join": "round",
    },
    paint: {
      "line-color": "#FFFFFF", // White stroke
      "line-opacity": 1,
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 7, 16, 11],
    },
  } as LayerProps

  // Gray outer casing layer
  const layerCasing = {
    id: `hovered-route-casing`,
    type: "line",
    layout: {
      "line-cap": "butt",
      "line-join": "round",
    },
    paint: {
      "line-color": "#6B7280", // Gray 500
      "line-opacity": 1,
      "line-width": ["interpolate", ["linear"], ["zoom"], 10, 9, 16, 13],
    },
  } as LayerProps

  return (
    <Source id={sourceId} type="geojson" data={feature}>
      <Layer {...layerCasing} />
      <Layer {...layerWhite} />
      <Layer {...layer} />
    </Source>
  )
}

export default HoveredRouteHighlight
